import { setupMarkdownToolPage } from './shared/index.js';
import { convertOfficeFile, inspectXlsxFile, OFFICE_MAX_INPUT_BYTES, requireUploadConsent } from './formats/docx/office-client.js';

function isXlsx(file) {
  return Boolean(file && (/\.xlsx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));
}

export function initXlsxPage(root = document) {
  const sheet = root.querySelector('#xlsx-sheet');
  const headerRow = root.querySelector('#xlsx-header-row');
  const maxRows = root.querySelector('#xlsx-max-rows');
  const displayValues = root.querySelector('#xlsx-display-values');
  const uploadConsent = root.querySelector('#upload-consent');
  let sourceFile = null;
  let inspectionController = null;
  let reading = false;
  let page;
  const clearSheetOptions = () => {
    if (!sheet) return;
    sheet.replaceChildren();
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'Choose after selecting a workbook';
    sheet.append(option);
  };
  const cancelInspection = () => {
    if (inspectionController) inspectionController.abort();
    inspectionController = null;
    reading = false;
    clearSheetOptions();
    page?.setBusy(false);
  };
  page = setupMarkdownToolPage({
    root,
    defaultFileName: 'xlsx-document',
    onFiles: async (files) => {
      const file = files?.[0];
      if (!file) return;
      try { requireUploadConsent(uploadConsent); } catch (error) { sourceFile = null; page.setReady(false); page.setStatus(error.message, 'error'); return; }
      if (files.length > 1) { page.setStatus('Choose one XLSX file at a time.', 'error'); return; }
      if (!isXlsx(file)) { page.setStatus('Choose an XLSX file.', 'error'); return; }
      if (!file.size || file.size > OFFICE_MAX_INPUT_BYTES) {
        page.setStatus('The XLSX file must be between 1 B and 50.0 MB.', 'error');
        return;
      }
      if (reading) return;
      reading = true;
      inspectionController?.abort();
      clearSheetOptions();
      const controller = new AbortController();
      inspectionController = controller;
      sourceFile = file;
      page.setBusy(true);
      // Sheet inspection itself is an upload task. Keep the consent control
      // enabled while it runs so revoking consent aborts this request too.
      if (uploadConsent) uploadConsent.disabled = false;
      page.setReady(false);
      page.setStatus('Reading worksheet names in the document service…');
      try {
        const inspected = await inspectXlsxFile(file, {
          signal: controller.signal,
          report: (message) => {
            if (inspectionController === controller && sourceFile === file && !controller.signal.aborted) page.setStatus(message);
          },
          consent: Boolean(uploadConsent?.checked),
        });
        if (inspectionController !== controller || controller.signal.aborted || sourceFile !== file || uploadConsent?.checked !== true) return;
        if (sheet) {
          sheet.replaceChildren();
          inspected.sheets.forEach((name, index) => {
            const option = document.createElement('option');
            option.value = name;
            option.textContent = name;
            if (index === 0) option.selected = true;
            sheet.append(option);
          });
        }
        page.setReady(inspected.sheets.length > 0);
        page.setStatus(inspected.sheets.length ? `${file.name} is ready. Choose a worksheet, then convert.` : 'No worksheet was found in this workbook.', inspected.sheets.length ? 'success' : 'error');
      } catch (error) {
        if (inspectionController === controller) {
          sourceFile = null;
          page.setReady(false);
          if (error?.name !== 'AbortError') page.setStatus(error instanceof Error ? error.message : 'The XLSX workbook could not be inspected.', 'error');
        }
      } finally {
        if (inspectionController === controller) {
          inspectionController = null;
          reading = false;
          page.setBusy(false);
        }
      }
    },
  });
  uploadConsent?.addEventListener('change', () => {
    if (!uploadConsent.checked) {
      cancelInspection();
      sourceFile = null;
      page.setReady(false);
      page.setStatus('Upload authorization removed; workbook inspection canceled.', 'error');
    }
    else if (sourceFile && !reading) page.setReady(true);
  });
  page.elements.cancelButton?.addEventListener('click', () => {
    if (!reading) return;
    cancelInspection();
    sourceFile = null;
    page.setReady(false);
    page.setStatus('Workbook inspection canceled.');
  });
  root.defaultView?.addEventListener('pagehide', () => {
    cancelInspection();
    page.gate.cancel('pagehide');
  }, { once: true });
  page.elements.convertButton?.addEventListener('click', () => {
    if (!sourceFile) return;
    try { requireUploadConsent(uploadConsent); } catch (error) { page.setStatus(error.message, 'error'); return; }
    const selectedSheet = sheet?.value || '';
    if (!selectedSheet) { page.setStatus('Choose a worksheet before converting.', 'error'); return; }
    const rowLimit = Number(maxRows?.value || 1000);
    if (!Number.isInteger(rowLimit) || rowLimit < 1 || rowLimit > 10000) { page.setStatus('Maximum data rows must be between 1 and 10,000.', 'error'); return; }
    const selectedHeader = Number(headerRow?.value || 1);
    if (!Number.isInteger(selectedHeader) || selectedHeader < 1 || selectedHeader > 100000) { page.setStatus('Header row must be between 1 and 100,000.', 'error'); return; }
    page.runConversion(async ({ signal, report }) => {
      const output = await convertOfficeFile(sourceFile, 'xlsx', {
        sheet: selectedSheet,
        headerRow: selectedHeader,
        maxRows: rowLimit,
        displayValues: displayValues ? Boolean(displayValues.checked) : true,
      }, { signal, report, consent: Boolean(uploadConsent?.checked) });
      report('Markdown is ready.', 1);
      return output;
    }).catch(() => {});
  });
  page.elements.clearButton?.addEventListener('click', () => {
    cancelInspection();
    sourceFile = null;
    page.setReady(false);
    page.result.clear();
    page.setStatus('Source cleared.');
  });
  page.setReady(false);
  return { page, get sourceFile() { return sourceFile; } };
}

if (typeof document !== 'undefined' && document.documentElement.dataset.markdownFormat === 'xlsx') initXlsxPage();
